import React, { useState, useEffect, FormEvent, useCallback, ComponentType, useRef } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { Text, Avatar, Button, Toast, Flexbox } from '@morressier/ts';

import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import debounce from 'lodash/debounce';
import uniqWith from 'lodash/uniqWith';
import { toast } from 'react-toastify';

import {
  deleteDiscussion,
  fetchDiscussions,
  postDiscussion,
  DiscussionsResponse,
} from 'api/discussions';
import { Comment } from 'components/Comment';
import { MentionsEditorProps, MentionsEvent } from 'components/Comment/MentionsEditor';
import { FeatureFlags } from 'config/featureFlags';
import { SimpleCard } from 'containers/SessionsList/styles';
import { ThemeOverides } from 'containers/styles';
import { useRegAndTicketingContext } from 'store/modules/eventTicketing';
import { PusherHandler } from 'store/modules/pusher';
import { logEvent } from 'utils/eventTracking';
import { useHasMounted } from 'utils/layout';
import { DISCUSSION_PRESENTATION_UPDATED, DISCUSSION_PRESENTATION_DELETED } from 'utils/pusher';

import { useTaggableUsers } from './hooks';
import SignUpBanner, { SessionBlockedQnA } from './SignUpBanner';
import { SimpleDropdown } from './SimpleDropdown';
import * as Styles from './styles';

const MentionsEditor: ComponentType<MentionsEditorProps> = dynamic(
  () => import('components/Comment/MentionsEditor'),
  { ssr: false },
);

type ISortOrder = 'ascending' | 'descending';

interface DiscussionPusherUpdate {
  action: 'create' | 'update' | 'approve'; // The action performed which triggered this pusher event
  discussionId: string;
  approvedAt?: string | undefined; // some discussions are approved by default while others are not
  parentId?: string; // replies have parent id
  authorId: string;
}

export const CommentsContainer: React.FC<{
  sessionId?: string;
  posterId: string;
  authUser?: MorressierUserProfile;
  canApprove?: boolean;
  authors?: SessionPosterUser[];
  speakers?: SessionPosterUser[];
  presenterId?: string;
  auto_approve_discussions?: boolean;
  isMainEditorFocused?: boolean;
  isRtmpStream?: boolean;
  isHappeningNow?: boolean;
  event?: MorressierEvent;
}> = props => {
  const [comment, setComment] = useState<string>('');
  const [rawContent, setRawContent] = useState<string>('');
  const [sort_order, set_sort_order] = useState<ISortOrder>('ascending');

  const { authUser, canApprove, authors, speakers, isMainEditorFocused = false, event } = props;

  const discussionPresentationUpdated = DISCUSSION_PRESENTATION_UPDATED(
    props.posterId,
    props.sessionId,
  );

  const discussionPresentationDeleted = DISCUSSION_PRESENTATION_DELETED(
    props.posterId,
    props.sessionId,
  );

  /** @deprecated We don't show Author flags anymore */
  const _checkAuthor = (id: string) => authors?.some(author => author?.id === id);
  const checkSpeaker = (id: string) => speakers?.some(speaker => speaker?.id === id);

  const getChipName = (id: string) => (checkSpeaker(id) ? 'Speaker' : '');
  const [postingComment, setPostingComment] = useState(false);
  const [discussionsResults, setDiscussionsResults] = useState<DiscussionsResponse | null>();
  const [resetEditor, setResetEditor] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const router = useRouter();
  const queryPosterId = router.query['poster-id'] as string;

  const { data } = useTaggableUsers(queryPosterId || props.posterId, props.sessionId);

  const getAllPossibleMentions = (discussions?: MorressierDiscussion[]) => {
    discussions?.forEach(discussion => {
      const { author } = discussion; // TODO: Backend should include discussion author department and organization from BE

      if (author && !data?.find(u => u.id === author.id || author._id)) {
        data?.push({
          name: author.full_name || 'No name',
          avatar: author.picture_url,
          id: author.id,
          department: author.department,
          organization: author.organization,
        });
      }

      if (discussion.replies?.length) {
        getAllPossibleMentions(discussion.replies);
      }
    });

    return data.filter(u => u.id !== authUser?.id);
  };

  const mentions = getAllPossibleMentions(discussionsResults?.discussions);

  const [mentionedUsersState, setMentionedUsersState] = useState<string[]>([]);
  const [isFetching, setisFetching] = useState(true);
  const [offset, setOffset] = useState(0);
  const offsetCount = useRef(0);

  /**
   * This method helps to clear the editor when a user submits the form
   * If a better way is found we can remove or update this method
   */
  const clearEditor = () => {
    setComment('');
    setMentionedUsersState([]);
    setResetEditor(true);
    setTimeout(() => {
      setResetEditor(false);
    });
  };

  // TODO: reimplement this with SWR
  const reloadDiscussions = useCallback(
    (posterId: string, order: ISortOrder) => {
      fetchDiscussions({
        poster_id: posterId || props.posterId,
        session_id: props.sessionId,
        offset: 0,
        limit: offset + 100,
        sort_order: order || sort_order,
      }).then(result => {
        if (result) {
          setisFetching(false);
          setDiscussionsResults({
            discussions: result.discussions,
            total: result.total,
          });

          offsetCount.current = result.discussions.length;
        }
      });
    },
    [offset, props.posterId, props.sessionId, sort_order],
  );

  // TODO: reimplement this with SWR
  const loadMoreDiscussions = useCallback(
    (order: ISortOrder) => {
      setisFetching(true);

      if (queryPosterId && queryPosterId !== props.posterId) {
        return setDiscussionsResults(null);
      }

      return fetchDiscussions({
        poster_id: queryPosterId || props.posterId,
        session_id: props.sessionId,
        offset,
        sort_order: order,
      })
        .then(result => {
          if (result) {
            setDiscussionsResults(prev => {
              const uniqueDiscusions = prev
                ? uniqWith([...result.discussions, ...prev?.discussions], (a, b) => a.id === b.id)
                : result.discussions;

              return { discussions: uniqueDiscusions, total: result.total };
            });

            offsetCount.current = result.discussions.length;
          }
          setisFetching(false);
        })
        .then(() => setisFetching(false))
        .catch(() => {
          setisFetching(false);
        });
    },
    [queryPosterId, props.posterId, props.sessionId, offset],
  );

  useEffect(() => {
    loadMoreDiscussions(sort_order);
  }, [loadMoreDiscussions, sort_order]);

  useEffect(() => {
    reloadDiscussions(props.posterId, sort_order);
  }, [props.posterId, reloadDiscussions, sort_order]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (postingComment) return;

    try {
      setPostingComment(true);

      await postDiscussion(
        {
          content: comment?.trim(),
          raw_content: rawContent.trim(),
          mentions: mentionedUsersState,
          session: props.sessionId,
          approved: !props.sessionId && !!props.auto_approve_discussions,
        },
        props.posterId,
      );

      setPostingComment(false);
      clearEditor();

      logEvent('STANDALONE_SUBMITTED_QUESTION', {
        presentation_id: props.posterId,
        session_id: props.sessionId,
      });

      if (props.isRtmpStream) {
        logEvent('STANDALONE_HYBRID_SUBMITTED_QUESTION', {
          presentation_id: props.posterId,
          session_id: props.sessionId,
        });
      }

      /**
       * only show the toast to people who can't approve
       * and when auto_approve_discussions is not enabled
       */
      if (props.auto_approve_discussions) return;

      if (!canApprove) {
        toast(() => (
          <Toast title="Your message has been sent, but it needs a moderators approval" />
        ));
      }
    } catch (error) {
      setPostingComment(false);
      toast(() => <Toast title="Sorry, we couldn't post your comment." />);
    }
  };

  const handleReply = async (
    parent_id: string,
    message: string,
    rawMessage: string,
    mentionedUsers: string[],
  ) => {
    try {
      await postDiscussion(
        {
          parent: parent_id,
          content: message?.trim(),
          raw_content: rawMessage.trim(),
          mentions: mentionedUsers,
          session: props.sessionId,
          approved: !props.sessionId && !!props.auto_approve_discussions,
        },
        props.posterId,
      );

      return true;
    } catch (error) {
      toast(() => <Toast title="Sorry, we couldn't post your comment." />);
      return false;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDiscussion(id);

      return true;
    } catch (error) {
      toast(() => <Toast title="Sorry, we couldn't delete this comment." />);
      return false;
    }
  };

  const handleContentChange = (e: MentionsEvent) => {
    if (e.rawString === '') {
      return setComment(e.rawString);
    }

    setComment(e.stringifiedEditorContent);
    setMentionedUsersState(e.mentions);
    return setRawContent(e.rawString);
  };

  const observerRef = useRef<HTMLDivElement | null>(null);

  const hasMounted = useHasMounted();
  const hasIntersectionObserver = hasMounted && typeof window.IntersectionObserver !== 'undefined';

  const loadMore = useCallback(() => {
    if (isFetching) return;

    if (discussionsResults) {
      const { total } = discussionsResults;
      const discussionsCount = discussionsResults?.discussions.length;

      if (discussionsCount < total) {
        setOffset(prev => prev + offsetCount.current);
      }
    }
  }, [discussionsResults, isFetching]);

  useEffect(() => {
    const observer = hasIntersectionObserver
      ? new IntersectionObserver(
          ([entry]) => {
            if (entry.intersectionRatio === 1) {
              loadMore();
            }
          },
          {
            rootMargin: '0px',
            threshold: 1,
          },
        )
      : undefined;

    const element = observerRef.current;

    if (element && observer) {
      observer.observe(element);
    }

    return () => (element && observer ? observer.unobserve(element) : undefined);
  }, [hasIntersectionObserver, loadMore]);

  const LoadMoreButton = () => {
    if (discussionsResults && discussionsResults?.discussions.length < discussionsResults.total) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '10px',
          }}
        >
          <Button onClick={() => loadMore()}>Load more</Button>
        </div>
      );
    }

    return null;
  };

  // For some reason sort_order's current value is not retained when called from pusher updates,
  // using set_sort_order helps us retain the current value
  const handlePusherUpdates = () =>
    set_sort_order(prev => {
      reloadDiscussions(props.posterId, prev);
      return prev;
    });

  // discussion GET endpoints are currently cached at 3 seconds.
  // For now, we'll debounce all FE calls to this endpoint at 3 seconds to make sure we get the most recent discussion data
  // TODO: after testing this, we'll reduce the cache time on discussions endpoint to around 800ms and debounce FE calls at 800ms
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPusherUpdates = useCallback(debounce(handlePusherUpdates, 1000), []);

  const { breakpoints } = useTheme();
  const isExtraSmallDevice = useMediaQuery(breakpoints.down('xs'));
  const { state } = useRegAndTicketingContext();

  const isTicketingFlagEnabled = FeatureFlags.Event_Ticketing.isEnabled();

  const canUserPostComment = () => {
    if (!isTicketingFlagEnabled) return !!authUser;

    // when there's a sessionId commentsContainer is used in session view
    if (props.sessionId) {
      // in ticketed session pages, auth users must be registered or own tickets to comment
      return state.isTicketedEvent ? !!authUser && !!state.userHasAccess : !!authUser;
    }

    // else it's used in article view, users only need to be logged in to comment, no event registeration or access criteria needed
    return !!authUser;
  };

  return (
    <SimpleCard color="white" style={{ padding: '24px', cursor: 'unset' }}>
      <Grid container spacing={2} className="mb2">
        <Grid item xs={12} sm={8}>
          <Text fontWeight="bold">Ask a Question</Text>
          <Text color="grey">Get involved to find out more about this Presentation.</Text>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Flexbox
            alignItems="center"
            justifyContent={isExtraSmallDevice ? 'flex-start' : 'flex-end'}
            style={{ height: '100%' }}
          >
            <SimpleDropdown
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              dropdownContainerStyle={{ minWidth: 'calc(100% + 1rem)', marginLeft: '-0.75rem' }}
              dropdownOptions={[
                { value: 'asc', label: 'All Comments' },
                { value: 'desc', label: 'Most Recent' },
              ]}
              onDropdownChange={({ value }: { value: 'asc' | 'desc'; label: string }) => {
                if (value === 'asc') {
                  return set_sort_order('ascending');
                }

                return set_sort_order('descending');
              }}
            >
              Ask a question
            </SimpleDropdown>
          </Flexbox>
        </Grid>
      </Grid>
      {canUserPostComment() && authUser ? (
        <Styles.FlexContainer className="mt4">
          <>
            <Avatar
              customSize="2.5rem"
              customFontSize="1.1rem"
              fullName={authUser.full_name}
              src={authUser.picture_url}
              style={{ margin: '0 0.5rem 0 0' }}
            />
            <Styles.FlexColumn as="form" onSubmit={handleCreate} style={{ width: '100%' }}>
              <ThemeOverides color={{ primaryBrand: '#424d5d' }}>
                <MentionsEditor
                  style={{ marginTop: 0, minHeight: '55px' }}
                  rows={3}
                  placeholder="Write a comment..."
                  mentions={mentions}
                  onChange={handleContentChange}
                  reset={resetEditor}
                  handleFocus={() => setIsFocused(true)}
                  handleBlur={() => setIsFocused(false)}
                  externalScroll={isMainEditorFocused}
                />
              </ThemeOverides>

              {(isFocused || comment?.trim().length > 0) && (
                <Flexbox justifyContent="flex-end">
                  <Button
                    style={{ marginRight: '0.5rem' }}
                    type="button"
                    onClick={() => {
                      clearEditor();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    style={{ alignSelf: 'flex-end' }}
                    color="primary"
                    loading={postingComment}
                    disabled={comment?.trim().length <= 0}
                  >
                    Comment
                  </Button>
                </Flexbox>
              )}
              {/* <div style={{ height: '2rem' }} /> */}
            </Styles.FlexColumn>
          </>
        </Styles.FlexContainer>
      ) : (
        <>{props.sessionId ? <SessionBlockedQnA /> : <SignUpBanner event={event} />}</>
      )}

      {isFetching && !discussionsResults ? (
        <Text>Loading discussions</Text>
      ) : (
        discussionsResults?.discussions?.map(discussion => {
          const sender = discussion.author;

          const replies = (discussion.replies ?? []).map(reply => {
            const replySender = reply.author;

            return {
              belongsToAuthUser: authUser?.id === replySender?.id,
              discussion: reply,
              authorChip: getChipName(replySender?.id || ''),
            };
          });

          return (
            <Comment
              key={discussion.id}
              authorChip={getChipName(sender?.id || '')}
              belongsToAuthUser={authUser?.id === sender?.id}
              canApprove={canApprove}
              discussion={discussion}
              replies={replies}
              handleReply={handleReply}
              handleDelete={handleDelete}
              mentions={mentions}
              canUserPostComment={canUserPostComment()}
            />
          );
        })
      )}
      {hasIntersectionObserver ? (
        <div ref={observerRef}>
          {isFetching && discussionsResults && <Text>Getting more comments...</Text>}
        </div>
      ) : (
        <LoadMoreButton />
      )}
      {discussionsResults && (
        <>
          <PusherHandler
            key={`${props.posterId}-update-handler`}
            channel={discussionPresentationUpdated.channelName}
            event={discussionPresentationUpdated.eventType}
            // eslint-disable-next-line consistent-return
            onUpdate={(payload: DiscussionPusherUpdate) => {
              if (canApprove || payload.approvedAt) {
                return debouncedPusherUpdates();
              }

              if (
                !payload.approvedAt &&
                payload.authorId === authUser?.id &&
                payload.action !== 'update'
              ) {
                return debouncedPusherUpdates();
              }
            }}
          />
          <PusherHandler
            key={`${props.posterId}-delete-handler`}
            channel={discussionPresentationDeleted.channelName}
            event={discussionPresentationDeleted.eventType}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onUpdate={(payload: DiscussionPusherUpdate & { action: 'delete' }) => {
              debouncedPusherUpdates();
            }}
          />
        </>
      )}
    </SimpleCard>
  );
};
